import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import {
  isHostInboundMessage,
  type HostOutboundMessage,
  type HostSession,
  type Theme,
} from '../interfaces/host';
import { setAuth, setToken, clearAuth, getAuthTokenExpiry } from '../services/tokenStore';
import {
  setRefresher,
  clearRefresher,
  hasRefresher,
  refreshAccessToken,
  type Refresher,
} from '../services/authRefresh';
import { clearCachedDevToken } from '../services/devSessionCache';
import { getRuntimeConfig } from '../services/runtimeConfig';

/** Refresh this many ms before the token actually expires. */
const REFRESH_SKEW_MS = 60_000;
/** How long to wait for the host to push a token after a `token-expiring` nudge. */
const HOST_REFRESH_TIMEOUT_MS = 10_000;

type HandshakeStatus = 'waiting' | 'ready' | 'error';

interface HostBridgeValue {
  status: HandshakeStatus;
  session: HostSession | null;
  error: string | null;
  /** Send a message up to the host (e.g. resize, lifecycle). No-op if standalone. */
  postToHost: (message: HostOutboundMessage) => void;
  /** Developer-mode only: establish a session without a host handshake. */
  devSignIn: (session: HostSession, refresher?: Refresher) => void;
  /**
   * Clears auth/session state and returns to the waiting state — the only
   * way back to DevModePanel (or a fresh host handshake) once signed in.
   * Needed for cases the proactive refresh scheduler can't recover from
   * (e.g. a dev-mode token that expired with no refresher attached, or a
   * refresh that failed outright): retrying alone can't fix a truly dead
   * token, so there has to be an explicit way to start over.
   */
  signOut: () => void;
  /** True when running standalone (not embedded in a host iframe). */
  isStandalone: boolean;
  /**
   * Bumps every time the host sends `archer-chat:end-session`. Consumers
   * should diff this against a ref to detect a new signal (the initial value
   * is not itself a signal).
   */
  endSessionSignal: number;
}

const HostBridgeContext = createContext<HostBridgeValue | null>(null);

/** Reads the origin allowlist from runtime config (see `services/runtimeConfig`); `*` disables the check (dev only). */
function parseAllowedOrigins(): { list: string[]; allowAll: boolean } {
  const { allowedOrigins, allowAllOrigins } = getRuntimeConfig();
  return { list: allowedOrigins, allowAll: allowAllOrigins };
}

function applyTheme(theme: Theme): void {
  document.documentElement.setAttribute('data-theme', theme);
  document.documentElement.style.colorScheme = theme;
}

/**
 * Bridges the embedding host and the chat app. Listens for the `init` handshake,
 * validates the sender origin, feeds auth into the in-memory token store, and
 * exposes the resolved session. Falls back to dev env vars when no host posts
 * an init within a short window so local development works standalone.
 */
export function HostBridgeProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<HandshakeStatus>('waiting');
  const [session, setSession] = useState<HostSession | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [endSessionSignal, setEndSessionSignal] = useState(0);
  const hostOriginRef = useRef<string | null>(null);
  // Resolver for an in-flight host refresh (set when we nudge, cleared on reply).
  const pendingHostRefreshRef = useRef<((token: string | null) => void) | null>(null);
  const { list: allowedOrigins, allowAll } = useMemo(parseAllowedOrigins, []);

  const postToHost = useCallback((message: HostOutboundMessage) => {
    const target = hostOriginRef.current;
    if (window.parent === window) return; // standalone, no host
    window.parent.postMessage(message, target ?? '*');
  }, []);

  // Host-mode refresher: nudge the host and wait for it to push a fresh token
  // (resolved by the token-refresh handler). Times out so a non-responding host
  // doesn't hang the refresh.
  const hostRefresher = useCallback<Refresher>(
    () =>
      new Promise<string | null>((resolve) => {
        pendingHostRefreshRef.current = resolve;
        postToHost({ type: 'archer-chat:token-expiring' });
        window.setTimeout(() => {
          if (pendingHostRefreshRef.current === resolve) {
            pendingHostRefreshRef.current = null;
            resolve(null);
          }
        }, HOST_REFRESH_TIMEOUT_MS);
      }),
    [postToHost],
  );

  // Developer mode: directly apply a session (no host handshake). Mirrors the
  // init path so the rest of the app behaves identically. An optional refresher
  // keeps the dev session alive without re-login.
  const devSignIn = useCallback((next: HostSession, refresher?: Refresher) => {
    setAuth(next.token, next.tenantId);
    applyTheme(next.theme);
    setRefresher(refresher ?? null);
    setSession(next);
    setError(null);
    setStatus('ready');
  }, []);

  const signOut = useCallback(() => {
    clearRefresher();
    clearAuth();
    clearCachedDevToken();
    setSession(null);
    setError(null);
    setStatus('waiting');
  }, []);

  const isStandalone = typeof window !== 'undefined' && window.parent === window;

  useEffect(() => {
    const isAllowed = (origin: string): boolean =>
      allowAll || origin === window.location.origin || allowedOrigins.includes(origin);

    const handleMessage = (event: MessageEvent) => {
      if (!isHostInboundMessage(event.data)) return;
      if (!isAllowed(event.origin)) {
        // Silently ignore messages from untrusted origins.
        return;
      }
      hostOriginRef.current = event.origin;
      const msg = event.data;

      switch (msg.type) {
        case 'archer-chat:init': {
          if (!msg.token || !msg.tenantId || !msg.chatbotId) {
            setError('Host handshake missing token, tenantId, or chatbotId.');
            setStatus('error');
            postToHost({
              type: 'archer-chat:error',
              code: 'INVALID_INIT',
              message: 'Missing token, tenantId, or chatbotId.',
            });
            return;
          }
          setAuth(msg.token, msg.tenantId);
          const theme = msg.theme ?? 'light';
          applyTheme(theme);
          setSession({
            token: msg.token,
            tenantId: msg.tenantId,
            chatbotId: msg.chatbotId,
            chatbotVersionId: msg.chatbotVersionId,
            locale: msg.locale ?? navigator.language ?? 'en-US',
            theme,
            permissions: msg.permissions ?? [],
            sessionId: msg.sessionId,
          });
          // Host owns auth — refresh by nudging it for a new token.
          setRefresher(hostRefresher);
          setError(null);
          setStatus('ready');
          postToHost({ type: 'archer-chat:init-ack', ok: true });
          break;
        }
        case 'archer-chat:token-refresh': {
          if (msg.token) {
            setToken(msg.token);
            setSession((prev) => (prev ? { ...prev, token: msg.token } : prev));
            // Resolve an in-flight nudge (if any) so the refresh promise settles.
            if (pendingHostRefreshRef.current) {
              const resolve = pendingHostRefreshRef.current;
              pendingHostRefreshRef.current = null;
              resolve(msg.token);
            }
          }
          break;
        }
        case 'archer-chat:theme': {
          applyTheme(msg.theme);
          setSession((prev) => (prev ? { ...prev, theme: msg.theme } : prev));
          break;
        }
        case 'archer-chat:end-session': {
          setEndSessionSignal((prev) => prev + 1);
          break;
        }
      }
    };

    window.addEventListener('message', handleMessage);
    // Tell the host we're mounted and ready to receive `init`.
    postToHost({ type: 'archer-chat:ready' });

    // Dev fallback: if no host handshake arrives, bootstrap from env vars so the
    // app is usable standalone during local development.
    const devToken = import.meta.env.VITE_DEV_TOKEN?.trim();
    const devTenant = import.meta.env.VITE_DEV_TENANT_ID?.trim();
    const devChatbot = import.meta.env.VITE_DEV_CHATBOT_ID?.trim();
    let timer: number | undefined;
    if (devToken && devTenant && devChatbot) {
      timer = window.setTimeout(() => {
        setSession((prev) => {
          if (prev) return prev; // a real host already initialised us
          setAuth(devToken, devTenant);
          applyTheme('light');
          setStatus('ready');
          return {
            token: devToken,
            tenantId: devTenant,
            chatbotId: devChatbot,
            locale: navigator.language ?? 'en-US',
            theme: 'light',
            permissions: [],
          };
        });
      }, 800);
    }

    return () => {
      window.removeEventListener('message', handleMessage);
      if (timer) window.clearTimeout(timer);
      clearRefresher();
      clearAuth();
    };
  }, [allowedOrigins, allowAll, postToHost, hostRefresher]);

  // Proactive refresh: once a session is active and a refresher is registered,
  // refresh shortly before the token's exp and reschedule from the new token.
  // Re-runs when the token changes (e.g. a host push) to reschedule.
  useEffect(() => {
    if (status !== 'ready' || !hasRefresher()) return;
    let timer: number | undefined;
    let cancelled = false;

    const schedule = () => {
      const exp = getAuthTokenExpiry();
      if (!exp) return; // unknown expiry → rely on the 401 retry instead
      const delay = Math.max(exp - Date.now() - REFRESH_SKEW_MS, 1000);
      timer = window.setTimeout(async () => {
        if (cancelled) return;
        await refreshAccessToken();
        if (!cancelled) schedule();
      }, delay);
    };
    schedule();

    return () => {
      cancelled = true;
      if (timer) window.clearTimeout(timer);
    };
  }, [status, session?.token]);

  const value = useMemo<HostBridgeValue>(
    () => ({
      status,
      session,
      error,
      postToHost,
      devSignIn,
      signOut,
      isStandalone,
      endSessionSignal,
    }),
    [status, session, error, postToHost, devSignIn, signOut, isStandalone, endSessionSignal],
  );

  return <HostBridgeContext.Provider value={value}>{children}</HostBridgeContext.Provider>;
}

export function useHostBridge(): HostBridgeValue {
  const ctx = useContext(HostBridgeContext);
  if (!ctx) throw new Error('useHostBridge must be used within a HostBridgeProvider');
  return ctx;
}
